/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2017, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
*/
define([
	'jquery',
	'../common/NonVIPREventTarget',
	'underscore'
], function( $, NonVIPREventTarget, _ ) {

	const EVENTTYPE = {
		CUSTOMDATA: 'customdata',
		DATAPOINT: 'datapoint',
		ITEMCLASS: 'itemclass',
		ITEM: 'item',
		SUMMARY: 'summary',
		EXPANDED: 'expanded'
	};

	const CELL_TYPE = {
		ROW: 'row',
		COLUMN: 'column'
	};

	const AREA_TYPE = {
		ROW: 'cell_edge_row',
		COLUMN: 'cell_edge_column',
		CELL: 'cell',
		MEASURE: 'values',
		SUMMARY: 'summary'
	};

	const SLOTID = {
		COLUMN : 'column_level1',
		ROW : 'row_level1',
		VALUES : 'values',
		HEAT: 'heat'
	};

	class CrosstabEventTarget extends NonVIPREventTarget {
		constructor (...args) {
			super(...args);
			const options = args[0] || {};
			this.ownerWidget = options.ownerWidget;
			this.view = options.view;
		}

		/**
		 * Get the array of event targets based on the event.
		 * Event target represents a data point object that corresponds to the event.
		 *
		 * @param event	Event object
		 */
		getEventTargets (event) {
			if(!this._isValidEvent(event)){
				return [];
			}
			const targets = [];
			const items = this._getEventTargetItem(event);
			_.each(items, (item) => {
				if(item){
					const area = $(item.source[0]).attr('area');
					// determine if current selection is a valid selected area
					const isAreaSelected = this._isValidSelectedArea(item);
					// title Action/Slot action requirs the info as part of selectionContext
					if (isAreaSelected) {
						item.selectionContext = this._updateSelectionContext(item.selectionContext);
					}
					targets.push({
						key: item.key,
						type: item.infoType,
						source: item.source,
						values: item.values,
						selectionContext: item.selectionContext,
						area: area,
						isAreaSelected: isAreaSelected
					});
				}
			});
			return targets;
		}

		_isValidSelectedArea(item){
			const $itemSource = item.source && item.source[0] && $(item.source[0]);
			if ($itemSource.hasClass('dashboard-grid-cell-selected')) {
				return $itemSource.attr('area') !== AREA_TYPE.SUMMARY;
			}
		}

		_updateSelectionContext(selectionContext){
			selectionContext = selectionContext || {};
			_.extend(selectionContext,{
				actions: {
					actionOptions: {
						TextAction:
						{
							isAreaSelected: true
						}
					}
				}
			});
			return selectionContext;
		}

		decorateTarget (items, name, value, isMultiSelect) {
			let decorated = false;
			//crosstab does support style 'hightlight', it only decorate for style 'implied_selection' because this style supports muti-select cells
			if (name === 'highlight') {
				return true;
			} else if (name === 'implied_selection') {
				const { visControl } = this.view;
				if (items && items.length > 0){
					const oSourceNode = items[0].source;//There will be only one items for crosstab
					if(oSourceNode) {
						const area = oSourceNode.attr('area');
						const isMeasureHeader = area === AREA_TYPE.MEASURE;
						const isSummary = area  === AREA_TYPE.SUMMARY;
						if (isMeasureHeader) {
							if (value) {
								if ( isMultiSelect ) {
									visControl.decorateMeasureHeader(oSourceNode[0]);
								} else {
									// Measures header decorating
									this._clearSelectionController().then(() => {
										visControl.clearSelection({ clearSelectionCaches: true });
										visControl.decorateMeasureHeader(oSourceNode[0]);
									});
								}
							} else {
								// Clearing measure header decorations (items is undefined on hover)
								visControl.resetMeasureHeaderDecorations();
							}
							decorated = true;
						}
						if (isSummary) {
							if (value) {
								// Highlight summary cells
								this._clearSelectionController().then(() => {
									visControl.clearSelection({ clearSelectionCaches: true });
									visControl.highlightSummary(oSourceNode[0]);
								});
							} else {
								// Clearing summary cells highlighting
								visControl.resetSummaryHighlight();
							}
							decorated = true;
						}
					}
				} else if (!items) {
					// Clearing measure header decorations (items is undefined on hover)
					visControl.resetMeasureHeaderDecorations();
					visControl.resetSummaryHighlight();
					decorated = true;
				}
			}
			return decorated;
		}

		_clearSelectionController() {
			const visualization = this.content.getFeature('Visualization');
			const mappedSlotList = visualization.getSlots().getMappedSlotList();
			const itemIds = [];
			_.each(mappedSlotList, function(slot) {
				const dataItemList = slot.getDataItemList();
				if (dataItemList[0].getType() === 'category') {
					_.each(dataItemList, dataitem => {
						itemIds.push(dataitem.getColumnId());
					});
				}
			});
			const visController = this.content.getFeature('InteractivityController.deprecated');

			return visController.select({
				itemIds: itemIds,
				tuple: [],
				command: 'update',
				slotsToClear: mappedSlotList
			});
		}

		remove () {
			this.ownerWidget = null;
			this.view = null;
			super.remove();
		}

		_getEventTargetItem (event){
			const target = this._getTargetNode(event);

			if(target && target.textContent) {
				const $eventTarget = $(target);
				const bIsCell = $eventTarget.attr('tuple');
				if(this.view.visControl.isCrosstaMeasureNodeSelected(target) || $eventTarget.attr('measure') === 'true'){
					return [this._getMeasureItemClassTarget(event)];
				} else if($eventTarget.attr('area') === AREA_TYPE.SUMMARY) {
					return [this._getSummaryItemTarget(event, bIsCell)];
				} else {
					return bIsCell? [this._getDatapointTarget(event)] : [this._getItemTarget(event)];
				}
			}
		}

		_getDatapointTarget (event){
			const oSelections = this.view.visControl.dataPointNodeToSlotsSelections(event);
			if(!oSelections){
				return null;
			}
			const attributeSelections = oSelections.attributeSelections;
			let factSelections = [];
			if (oSelections.selectionContext) {
				// The selectionContext in a datapoint oSelection is the mapIndex of the data item that the datapoint corresponds to
				factSelections[oSelections.selectionContext] = oSelections.factSelections && oSelections.factSelections[0] ? oSelections.factSelections[0].value : [];
			} else {
				factSelections = oSelections.factSelections && oSelections.factSelections[0] ? [oSelections.factSelections[0].value] : [];
			}
			const aColumnValues = _.pluck(_.filter(attributeSelections, entry => entry.edgeType === CELL_TYPE.COLUMN), 'value');
			const aRowValues = _.pluck(_.filter(attributeSelections, entry => entry.edgeType === CELL_TYPE.ROW), 'value');
			const heatSelections = oSelections.factSelections && oSelections.factSelections[1] ? [oSelections.factSelections[1].value] : [];

			const oItem = this._createItem(EVENTTYPE.DATAPOINT, event);
			this._addItemValues(oItem.values, aColumnValues, aRowValues, factSelections, heatSelections);

			// Disable selection actions for events containing expanded items
			// This is due to limitations for brushing/slicing on expanded items for expand/collapse feature
			if (this._includesExpanded(aColumnValues, aRowValues)) {
				oItem.infoType = EVENTTYPE.EXPANDED;
				oItem.values = [];
				$(event.currentTarget).addClass('hoverExpanded');
			} else {
				$(event.currentTarget).removeClass('hoverExpanded');
			}

			return oItem;
		}

		_getItemTarget (event){
			const oSelections = this.view.visControl.nodeToSelection(event);
			if(!oSelections){
				return null;
			}
			const aColumnValues = oSelections.column_level1 ? oSelections.column_level1.value : [];
			const aRowValues = oSelections.row_level1 ? oSelections.row_level1.value : [];
			const aFactValues = oSelections.datapoint ? [oSelections.datapoint] : [];
			const aItemClassValues = oSelections.itemClassValues ? oSelections.itemClassValues : [];
			const sType = oSelections.isInnerEdge === true ? EVENTTYPE.DATAPOINT : EVENTTYPE.ITEM;
			const oItem = this._createItem(sType, event);

			// If itemClass target, obtain the item values first. The items are then used to create
			// the selection context in _getMeasureSelectionContext
			if (aItemClassValues.length > 0) {
				oItem.values = aItemClassValues;
				oItem.selectionContext = this._getMeasureSelectionContext(oItem.values);
			} else {
				const oSelectionContext = this._getSelectionContext(oSelections.selectionContext);
				if(oSelectionContext){
					oItem.selectionContext = oSelectionContext;
					oItem.selectionContext.actions.actionOptions = {
						deleteAction: { excludeAction: true },
						jumpToAction: { area: event.target.getAttribute('area') }
					};
				}
				this._addItemValues(oItem.values, aColumnValues, aRowValues, aFactValues);
			}
			// Disable selection actions for events containing expanded items
			// This is due to limitations for brushing/slicing on expanded items for expand/collapse feature
			if (this._includesExpanded(aColumnValues, aRowValues)) {
				oItem.infoType = EVENTTYPE.EXPANDED;
				oItem.values = [];
				$(event.currentTarget).addClass('hoverExpanded');
			} else {
				$(event.currentTarget).removeClass('hoverExpanded');
			}

			return oItem;
		}

		_getMeasureSelectionContext (oItemValues) {
			const selectionContext = {
				actions: { actionsToKeep: [], actionOptions: {} }
			};
			selectionContext.slots = [this.visualization.getSlots().getSlot(SLOTID.VALUES)];
			selectionContext.mapIndex = [];
			_.each(oItemValues, function(slot) {
				_.each(slot, function(dataItem) {
					selectionContext.mapIndex.push(dataItem.mapIndex);
				});
			});

			return selectionContext;
		}

		_getSelectionContext (selectionContext) {
			if(selectionContext.level === undefined){
				return null;
			}
			const oSlot = this.visualization.getSlots().getSlot(selectionContext.slotID);
			const aColumnItemAPIs = oSlot && oSlot.getDataItemList();
			if(aColumnItemAPIs && aColumnItemAPIs.length > 0 && selectionContext.level < aColumnItemAPIs.length){
				return {
					'mapIndex' : selectionContext.level,
					'slots': [oSlot],
					'actions': { actionsToKeep: [], actionOptions: {} }
				};
			}
		}

		_createItem (itemType, event) {
			const oItem = {};
			oItem.infoType = itemType;
			const $target = $(this._getTargetNode(event));

			oItem.key = $target.attr('row') + ':' + $target.attr('col');
			oItem.source = $target;
			oItem.values = [];

			return oItem;
		}

		_addItemValues (itemValues, colValues, rowValues, factValues, heatValues) {
			const aSlots = this.visualization.getSlots().getMappedSlotList();//Mapped Slots only
			_.each(aSlots, function(entry){
				const id = entry.getId();
				if(id === SLOTID.COLUMN){
					itemValues.push(colValues);
				}else if(id === SLOTID.ROW){
					itemValues.push(rowValues); //Required Slot
				}else if(id === SLOTID.VALUES){
					itemValues.push(factValues); //Required Slot
				}else if(id === SLOTID.HEAT){
					itemValues.push(heatValues);
				}
			});
		}

		_getMeasureItemClassTarget (event) {
			const itemTarget = this._getItemTarget(event);
			itemTarget.infoType = EVENTTYPE.ITEMCLASS;
			return itemTarget;
		}

		_getSummaryItemTarget(event, isCell) {
			let itemTarget =  isCell ? null : this._getItemTarget(event);
			if (itemTarget){
				itemTarget.infoType = EVENTTYPE.SUMMARY;
				itemTarget.selectionContext = {
					actions: {
						actionsToKeep: ['text'],
						actionOptions: {
							TextAction:
							{
								area: AREA_TYPE.SUMMARY
							}
						}
					}
				};
			}
			return itemTarget;
		}

		_getUnionValues() {
			let dataItems = this.view.content.getFeature('Visualization').getSlots().getDataItemList();
			let drillDownValue;
			let unionItems = [];
			let drillDownValues = [];

			// Get first level union item list and slot drill down values
			_.forEach(dataItems, (dataItem) => {
				drillDownValue = dataItem.getDrillDownValue();
				if (drillDownValue) { drillDownValues.push(drillDownValue); }
				if (dataItem.getUnion) {
					unionItems.push(...dataItem.getUnion().getDataItemList());
				}
			});

			let item;
			let values = [];
			// DFS on union model
			while (!_.isEmpty(unionItems)) {
				item = unionItems.pop();
				drillDownValue = item.getDrillDownValue();
				if (drillDownValue && !_.includes(drillDownValues, drillDownValue)) { values.push(drillDownValue); }
				if (item.getUnion) {
					unionItems.push(...item.getUnion().getDataItemList());
				}
			}
			return values;
		}

		_includesExpanded(aColumnValues, aRowValues) {
			let eventValues = [];
			// value.pu is for level based hiearchy; value.p.u is for member based hierachy
			eventValues.push(...aColumnValues.reduce(function (arr, value) {
				if (value.pu) {
					arr.push(value.pu);
				} else if (value.p && value.p.u) {
					arr.push(value.p.u);
				}
				return arr;
			}, []));

			eventValues.push(...aRowValues.reduce(function (arr, value) {
				if (value.pu) {
					arr.push(value.pu);
				} else if (value.p && value.p.u) {
					arr.push(value.p.u);
				}
				return arr;
			}, []));

			let unionValues = this._getUnionValues();
			return (!_.isEmpty(_.intersection(eventValues, unionValues)));
		}

		_isValidEvent (event) {
			const _isTargetValidGridCell = (eventTarget) => {
				return eventTarget.localName === 'div' &&
					($(eventTarget).hasClass('bottom-right-grid-cell') ||
						$(eventTarget).hasClass('top-right-grid-cell') ||
						$(eventTarget).hasClass('bottom-left-grid-cell') ||
						$(eventTarget).hasClass('top-left-grid-cell'));
			};

			const _fnClickTapCheck = (eventTarget) => {
				return _isTargetValidGridCell(eventTarget);

			};
			const _fnContextMenuCheck = (eventTarget) => {
				return _isTargetValidGridCell(eventTarget);

			};
			const _fnMouseMoveCheck = (eventTarget) => {
				return _isTargetValidGridCell(eventTarget);

			};

			const _fnMouseLeaveCheck = (eventTarget)=>{
				return this.mouseWithinBoundCheck(event) && _isTargetValidGridCell(eventTarget);
			};

			const _fnKeydownCheck = (eventTarget) => {
				if(_isTargetValidGridCell(eventTarget)){
					return true;
				}
				return false;
			};

			const oValidEvents = {
				click: _fnClickTapCheck,
				clicktap: _fnClickTapCheck,
				mousedown: _fnClickTapCheck,
				tap: _fnClickTapCheck,
				contextmenu:  _fnContextMenuCheck,
				hold: _fnContextMenuCheck,
				mousemove: _fnMouseMoveCheck,
				mouseleave:  _fnMouseLeaveCheck.bind(this),
				keydown: _fnKeydownCheck
			};

			const fnCheck = oValidEvents[event.type];
			const target = this._getTargetNode(event);
			return fnCheck && fnCheck(target) === true;
		}
	}
	return CrosstabEventTarget;
});
